import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { useCrumb } from "@poseidon/ui/app/crumbs/CrumbContext";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { Panel } from "@poseidon/ui/components";
import DatePicker from "@poseidon/ui/components/form/DatePicker";
import { FullWidthField } from "@poseidon/ui/components/form/FullWidthField";
import { useDoc } from "@poseidon/firebase";
import { getDate } from "@poseidon/firebase/timestamp";
import { useBaseRoutePathContext } from "@poseidon/utils/base-route-path";
import { transformDateForYup } from "@poseidon/utils/yup";
import { Field, Form, Formik } from "formik";
import { Switch } from "formik-mui";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { boolean, date, object, string } from "yup";
import * as api from "../api";

export default function AddressManagement() {
  const baseRoutePath = useBaseRoutePathContext();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { addressId, sailorId } = useParams();
  const {
    update,
    loading,
    loadError,
    data: address,
    saveError,
  } = useDoc("sailorAddresses", addressId);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const crumb = {
    to: `${baseRoutePath.route}/sailors/:sailorId/addresses`,
    text: "addresses",
  };

  useCrumb(`${baseRoutePath.route}/sailors/:sailorId/addresses`, crumb);

  const addAddressHandler = async (values) => {
    await api.addAddress(values, sailorId);
    setToast("Address added");
  };

  const editAddressHandler = async (values) => {
    const result = await update(values);
    if (!result) {
      throw new Error("Error saving address");
    }
    setToast("Address updated");
  };

  const onSubmitHandler = async (values) => {
    setSaving(true);
    try {
      addressId
        ? await editAddressHandler(values)
        : await addAddressHandler(values);
      navigate(`${baseRoutePath.path}/sailors/${sailorId}`);
    } catch (error) {
      setError(error.message);
    }
    setSaving(false);
  };

  useEffect(() => {
    if (loadError) {
      setError(loadError);
    }
  }, [loadError, setError]);

  useEffect(() => {
    if (saveError) {
      setError(saveError);
    }
  }, [saveError, setError]);

  if (loading || (addressId && !address)) {
    return <LinearProgress />;
  }

  const addressSchema = object().shape(
    {
      houseName: string()
        .max(50)
        .when("houseNumber", {
          is: (houseNumber) => !houseNumber,
          then: (schema) =>
            schema.required("House name is required if House number is empty"),
          otherwise: (schema) => schema.optional(),
        }),
      houseNumber: string()
        .max(10)
        .when("houseName", {
          is: (houseName) => !houseName,
          then: (schema) =>
            schema.required("House number is required if House Name is empty"),
          otherwise: (schema) => schema.optional(),
        }),
      flat: string().max(30),
      street_1: string().max(60).required("Address is required"),
      street_2: string().max(60),
      town: string().max(30),
      region: string().max(30),
      postcode: string().max(8).required("Postcode is required"),
      from: date()
        .transform(transformDateForYup)
        .required("From date is required"),
      current: boolean().required("Need to mark is current address or not"),
      to: date()
        .transform(transformDateForYup)
        .when("current", {
          is: (current) => current === true,
          then: (schema) => schema.optional(),
          otherwise: (schema) =>
            schema.required(
              "Either mark as current address, or specify a to date"
            ),
        }),
    },
    [["houseNumber", "houseName"]]
  );

  const initialValues = addressId
    ? {
        ...address,
        from: getDate(address.from),
        to: getDate(address.to),
      }
    : {
        houseNumber: "",
        houseName: "",
        flat: "",
        street_1: "",
        street_2: "",
        town: "",
        region: "",
        postcode: "",
        from: null,
        to: null,
        current: false,
      };
  return (
    <Stack spacing={5} sx={{ px: 5, mb: 5 }}>
      <Panel title={addressId ? "Update Address" : "Create Address"}>
        <Formik
          initialValues={initialValues}
          validationSchema={addressSchema}
          onSubmit={onSubmitHandler}
        >
          {({ values, errors, touched, isSubmitting, dirty }) => (
            <Form>
              <Grid container spacing={2} my={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="houseNumber"
                    label="House Number"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.houseNumber && "House number required"}
                    error={!!(errors.houseNumber && touched.houseNumber)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="houseName"
                    label="House Name"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.houseName && "House Name required"}
                    error={!!(errors.houseName && touched.houseName)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="flat"
                    label="Flat Number"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.flat && "Flat required"}
                    error={!!(errors.flat && touched.flat)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="street_1"
                    label="Address Line 1"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.street_1 && "Address Line 1 required"}
                    error={!!(errors.street_1 && touched.street_1)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="street_2"
                    label="Address Line 2"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.street_2 && "Address Line 2 invalid"}
                    error={!!(errors.street_2 && touched.street_2)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="town"
                    label="Town / City"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.town && "Town / City required"}
                    error={!!(errors.town && touched.town)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="region"
                    label="Region"
                    margin="none"
                    disabled={isSubmitting}
                    helperText={errors.region && "Region invalid"}
                    error={!!(errors.region && touched.region)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={FullWidthField}
                    name="postcode"
                    label="Postcode"
                    margin="none"
                    helperText={errors.postcode && "Postcode required"}
                    error={!!(errors.postcode && touched.postcode)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={DatePicker}
                    name="from"
                    label="Date From"
                    margin="none"
                  />
                </Grid>
                {!values.current && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={DatePicker}
                      name="to"
                      label="Date To"
                      margin="none"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption">Is current Address</Typography>
                  <Field
                    component={Switch}
                    type="checkbox"
                    name="current"
                    helperText={errors.current && "Mark current address"}
                    error={!!(errors.current && touched.current)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  margin="normal"
                  disabled={saving || !dirty}
                >
                  {addressId ? "Update Address" : "Create Address"}
                </Button>
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>
    </Stack>
  );
}
